import getMode from "../tools/getMode";
import mergeDeep from "../tools/merge";
import downloadURI from "../tools/downloadURI";
import QRSVG from "./QRSVG";
import drawTypes from "../constants/drawTypes";

import defaultOptions, { RequiredOptions } from "./QROptions";
import sanitizeOptions from "../tools/sanitizeOptions";
import { Extension, QrMatrix, Options, DownloadOptions } from "../types";

// ESM/CJS craziness replaces the line below with a quick evaluation to see if "qrcode" is a function or module.default;
import * as qrcodeImport from "qrcode-generator";
const oldQr = typeof qrcodeImport === "function" ? qrcodeImport : qrcodeImport.default;
import { qrcode as newQr } from "./QRCode";

export default class QRCodeStyling {
  _options: RequiredOptions;
  _container?: HTMLElement;
  _svg?: QRSVG;
  _qr?: QrMatrix;
  _svgDrawingPromise?: Promise<void>;

  constructor(options?: Partial<Options>) {
    this._options = options ? sanitizeOptions(mergeDeep(defaultOptions, options) as RequiredOptions) : defaultOptions;
    this.update();
  }

  static _clearContainer(container?: HTMLElement): void {
    if (container) {
      container.innerHTML = "";
    }
  }

  async _getQRStylingElement(extension: Extension = "png"): Promise<QRSVG> {
    if (!this._qr) throw "QR code is empty";
    let promise, svg: QRSVG;

    if (this._svg && this._svgDrawingPromise) {
      svg = this._svg;
      promise = this._svgDrawingPromise;
    } else {
      svg = new QRSVG(this._options);
      promise = svg.drawQR(this._qr);
    }

    await promise;

    return svg;

  }

  update(options?: Partial<Options>): void {
    QRCodeStyling._clearContainer(this._container);
    this._options = options ? sanitizeOptions(mergeDeep(this._options, options) as RequiredOptions) : this._options;

    if (!this._options.data) {
      return;
    }

    const qrcode = (this._options.engine === "legacy") ? oldQr : newQr;
    if (this._options.engine === "legacy") {
      console.log("legacy engine")
    }
    this._qr = qrcode(this._options.qrOptions.typeNumber, this._options.qrOptions.errorCorrectionLevel);
    this._qr.addData(this._options.data, this._options.qrOptions.mode || getMode(this._options.data));
    this._qr.make();

    if (this._options.type === drawTypes.canvas) {
      throw new Error(
        "omitting QR Canvas from build, re-enable here (change from 'import type' to 'import' above) if required"
      );
      // this._canvas = new QRCanvas(this._options);
      // this._canvasDrawingPromise = this._canvas.drawQR(this._qr);
      // this._svgDrawingPromise = undefined;
      // this._svg = undefined;
    } else {
      this._svg = new QRSVG(this._options);
      this._svgDrawingPromise = this._svg.drawQR(this._qr);
    }

    this.append(this._container);
  }

  append(container?: HTMLElement): void {
    if (!container) {
      return;
    }

    if (typeof container.appendChild !== "function") {
      throw "Container should be a single DOM node";
    }

    if (this._options.type === drawTypes.canvas) {
      throw new Error("Canvas QR rendering is not included in this build; use SVG output.");
    } else {
      if (this._svg) {
        container.appendChild(this._svg.getElement());
      }
    }

    this._container = container;
  }

  /**
   * Worker-safe: returns the raw SVG markup string without touching the DOM.
   * Use this in Web Workers or anywhere DOMParser/XMLSerializer are unavailable.
   */
  async getRawSvgString(): Promise<string> {
    if (!this._qr) throw "QR code is empty";
    const svg = await this._getQRStylingElement("svg");
    return svg.getSvgString();
  }

  async getRawVectorPath(): Promise<string> {
    const svg = await this._getQRStylingElement("svg");
    return svg.getVectorPath();
  }

  async getRawData(extension: Extension = "png"): Promise<Blob | null> {
    if (!this._qr) throw "QR code is empty";
    const element = await this._getQRStylingElement(extension);

    if (extension.toLowerCase() === "svg") {
      const source = (element as QRSVG).getSvgString();
      return new Blob(['<?xml version="1.0" standalone="no"?>\r\n' + source], { type: "image/svg+xml" });
    } else {
      throw new Error("Raster QR output is not included in this build; use SVG output.");
    }
  }

  async download(downloadOptions?: Partial<DownloadOptions> | string): Promise<void> {
    if (!this._qr) throw "QR code is empty";
    let extension = "png" as Extension;
    let name = "qr";

    //TODO remove deprecated code in the v2
    if (typeof downloadOptions === "string") {
      extension = downloadOptions as Extension;
      console.warn(
        "Extension is deprecated as argument for 'download' method, please pass object { name: '...', extension: '...' } as argument"
      );
    } else if (typeof downloadOptions === "object" && downloadOptions !== null) {
      if (downloadOptions.name) {
        name = downloadOptions.name;
      }
      if (downloadOptions.extension) {
        extension = downloadOptions.extension;
      }
    }

    const element = await this._getQRStylingElement(extension);

    if (extension.toLowerCase() === "svg") {
      let source = (element as QRSVG).getSvgString();
      source = '<?xml version="1.0" standalone="no"?>\r\n' + source;
      const url = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(source);
      downloadURI(url, `${name}.svg`);
    } else {
      throw new Error("Raster QR output is not included in this build; use SVG output.");
    }
  }
}
